import { useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import Drawer from "../../components/ui/Drawer";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useGetAssessmentResultsQuery } from "../../api/muzakereApi";
import {
  answerText,
  averagePercent,
  effectiveState,
  hasFullAnswers,
  percentOf,
  questionStats,
  responseAnswers,
  scoreText,
  verdictOf,
} from "../../lib/assessment";
import { SoftBar, SoftButton, cls } from "../hatim/soft/SoftKit";
import { TYPE_LABEL } from "./questionRules";
import ConnectionState from "../../components/ui/ConnectionState";

const fullName = (p) => `${p?.name || ""} ${p?.surname || ""}`.trim();

const timeText = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const GREEN = "text-[#2E9E6A] dark:text-[#7BE3A8]";
const BAR_GREEN = "#2E9E6A";
const BAR_MUTED = "#8FC9CA";

function Section({ title, children }) {
  return (
    <section className="flex flex-col">
      <h4 className={`px-5 pt-5 pb-2 text-xs uppercase tracking-wide ${cls.muted}`}>{title}</h4>
      {children}
    </section>
  );
}

/** Seçenek satırı: ad, sayı ve ince çubuk; doğru seçenek yeşil. */
function OptionBar({ label, count, total, correct }) {
  const pct = percentOf(count, total);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs">
        {correct ? <CheckCircleIcon sx={{ fontSize: 13 }} className={GREEN} /> : null}
        <span className={`flex-1 min-w-0 break-words ${correct ? GREEN : cls.title}`}>{label}</span>
        <span className={cls.muted}>{`${count} · %${pct}`}</span>
      </div>
      <SoftBar pct={pct} color={correct ? BAR_GREEN : BAR_MUTED} height={4} />
    </div>
  );
}

/** Soru bazlı istatistik: seçenek dağılımı, doğru oranı; açık uçluda cevaplar. */
function QuestionStat({ q, index, stat, submitted, detailed, t }) {
  const [open, setOpen] = useState(false);
  const boolKeys = q.type === "evethayir" ? ["assess_yes", "assess_no"] : ["assess_true", "assess_false"];
  return (
    <li className={`px-5 py-3 flex flex-col gap-2 ${cls.row}`}>
      <div className="flex flex-col gap-0.5">
        <span className={`text-sm ${cls.title}`}>{`${index + 1}. ${q.text}`}</span>
        <span className={`text-xs ${cls.muted}`}>
          {[
            t(TYPE_LABEL[q.type]),
            detailed || q.type === "acik" ? t("mzr_answered_count", { n: stat.answered, total: submitted }) : null,
            detailed && stat.graded ? t("mzr_correct_rate", { pct: percentOf(stat.correct, stat.answered) }) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      {q.type === "acik" ? (
        stat.texts.length ? (
          <>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className={`self-start flex items-center gap-1 text-xs ${cls.accent}`}
            >
              {t(open ? "mzr_hide_answers" : "mzr_show_answers")}
              <ExpandMoreIcon sx={{ fontSize: 16 }} className={open ? "rotate-180" : ""} />
            </button>
            {open ? (
              <ul className="flex flex-col gap-2 pl-2 border-l-2 border-terakkiBrandMist dark:border-white/15">
                {stat.texts.map((a) => (
                  <li key={a.userId} className="flex flex-col">
                    <span className={`text-xs ${cls.muted}`}>{a.name}</span>
                    <span className={`text-sm whitespace-pre-line break-words ${cls.title}`}>{a.text}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null
      ) : detailed ? (
        <div className="flex flex-col gap-2">
          {q.type === "coktan"
            ? (q.options || []).map((option, i) => (
                <OptionBar
                  key={i}
                  label={option}
                  count={stat.counts[i]}
                  total={stat.answered}
                  correct={q.correctIndex === i}
                />
              ))
            : boolKeys.map((key, i) => (
                <OptionBar
                  key={key}
                  label={t(key)}
                  count={stat.counts[i]}
                  total={stat.answered}
                  correct={q.correctBool === (i === 0)}
                />
              ))}
        </div>
      ) : null}
    </li>
  );
}

/** Gönderen satırı: ad, puan, gönderim saati; basınca her sorudaki cevabı. */
function ResponseRow({ r, questions, t }) {
  const [open, setOpen] = useState(false);
  const answers = responseAnswers(r);
  const pct = r.score?.total > 0 ? percentOf(r.score.correct, r.score.total) : null;
  return (
    <li className={cls.row}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full text-left px-5 py-3 flex items-center gap-3 ${cls.rowHover}`}
      >
        <div className="min-w-0 flex-1">
          <div className={`text-sm truncate ${cls.title}`}>{fullName(r) || "..."}</div>
          {r.submittedAt ? <div className={`text-xs ${cls.muted}`}>{timeText(r.submittedAt)}</div> : null}
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm ${cls.title}`}>{scoreText(r.score, t)}</div>
          {pct !== null ? <div className={`text-xs ${cls.muted}`}>{`%${pct}`}</div> : null}
        </div>
        <ExpandMoreIcon fontSize="small" className={`${cls.muted} transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <ul className="px-5 pb-3 flex flex-col gap-2">
          {questions.map((q, i) => {
            const a = answers.get(String(q.qid));
            const text = answerText(q, a, t);
            const verdict = a ? verdictOf(q, a) : null;
            return (
              <li key={q.qid} className="flex flex-col gap-0.5">
                <span className={`text-xs ${cls.muted}`}>{`${i + 1}. ${q.text}`}</span>
                <span className={`flex items-start gap-1.5 text-sm ${cls.title}`}>
                  {verdict === true ? <CheckCircleIcon sx={{ fontSize: 15, mt: "2px" }} className={GREEN} /> : null}
                  {verdict === false ? <CancelIcon sx={{ fontSize: 15, mt: "2px" }} className={cls.danger} /> : null}
                  <span className={`break-words whitespace-pre-line ${text ? "" : cls.muted}`}>
                    {text || t(Array.isArray(r.answers) ? "assess_unanswered" : "mzr_answer_unknown")}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Yönetici: değerlendirme sonuçları (mobil muzakere/results/[id], web'de
 * soru bazlı istatistik ile genişletildi). Özet, sorular (seçenek dağılımı,
 * doğru oranı, açık uçlu cevaplar), gönderenler (puan, basınca cevapları)
 * ve göndermeyenler.
 */
export default function AssessmentResultsDrawer({ open, muzakere, onClose }) {
  const { t } = useScriptMode();
  const { data, isLoading, isFetching, isError, error, refetch } = useGetAssessmentResultsQuery(
    { muzakereId: muzakere?.id },
    { skip: !open || !muzakere?.id, refetchOnMountOrArgChange: true }
  );
  const result = data?.data;
  const questions = useMemo(() => result?.questions || [], [result]);
  const responses = useMemo(() => result?.responses || [], [result]);
  const notSubmitted = result?.notSubmitted || [];
  const stats = useMemo(() => questionStats(questions, responses), [questions, responses]);
  const detailed = hasFullAnswers(responses);
  const total = responses.length + notSubmitted.length;
  const average = averagePercent(responses);
  const state = result ? effectiveState(result.state, result.closesAt) : null;

  let body;
  if (!result) {
    body = isLoading || isFetching ? (
      <div className="p-5 flex flex-col gap-3">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-5 rounded ${cls.skeleton}`} />
        ))}
      </div>
    ) : isError ? (
      <ConnectionState error={error} onRetry={refetch} message={t("mzr_load_failed")} compact />
    ) : (
      <div className="flex flex-col items-center gap-3 py-14 px-6 text-center">
        <p className={`text-sm ${cls.muted}`}>{t("assess_none")}</p>
        <SoftButton tone="soft" onClick={() => refetch()}>
          {t("retry")}
        </SoftButton>
      </div>
    );
  } else {
    body = (
      <>
        <div className={`px-5 py-4 flex flex-wrap gap-x-8 gap-y-2 ${cls.summary}`}>
          <div>
            <div className={`text-xs ${cls.muted}`}>{t("mzr_submitted")}</div>
            <div className={`text-xl ${cls.heading}`}>{`${responses.length}/${total}`}</div>
          </div>
          <div>
            <div className={`text-xs ${cls.muted}`}>{t("mzr_average")}</div>
            <div className={`text-xl ${cls.heading}`}>{average === null ? "-" : `%${average}`}</div>
          </div>
          <div>
            <div className={`text-xs ${cls.muted}`}>{t("mzr_questions")}</div>
            <div className={`text-xl ${cls.heading}`}>{questions.length}</div>
          </div>
          <div className="flex items-end">
            <span className={`text-xs ${cls.muted}`}>{t(state === "open" ? "mzr_state_open" : "mzr_state_closed")}</span>
          </div>
        </div>

        <Section title={`${t("mzr_questions")} · ${questions.length}`}>
          {!detailed && responses.length > 0 ? (
            <p className={`px-5 pb-2 text-xs ${cls.muted}`}>{t("mzr_old_server_hint")}</p>
          ) : null}
          <ul className="border-t border-terakkiBorder dark:border-white/[0.14]">
            {questions.map((q, i) => (
              <QuestionStat
                key={q.qid}
                q={q}
                index={i}
                stat={stats[i]}
                submitted={responses.length}
                detailed={detailed}
                t={t}
              />
            ))}
          </ul>
        </Section>

        <Section title={`${t("mzr_submitted")} · ${responses.length}`}>
          {responses.length === 0 ? (
            <p className={`px-5 py-2 text-sm ${cls.muted}`}>{t("mzr_none_submitted")}</p>
          ) : (
            <ul className="border-t border-terakkiBorder dark:border-white/[0.14]">
              {responses.map((r) => (
                <ResponseRow key={r.userId} r={r} questions={questions} t={t} />
              ))}
            </ul>
          )}
        </Section>

        <Section title={`${t("mzr_not_submitted")} · ${notSubmitted.length}`}>
          {notSubmitted.length === 0 ? (
            <p className={`px-5 py-2 text-sm ${cls.muted}`}>{t("mzr_everyone_submitted")}</p>
          ) : (
            <ul className="border-t border-terakkiBorder dark:border-white/[0.14]">
              {notSubmitted.map((p) => (
                <li key={p.userId} className={`px-5 py-2.5 text-sm ${cls.muted} ${cls.row}`}>
                  {fullName(p) || "..."}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title={t("mzr_title")}
      subtitle={muzakere?.title}
      titleIcon={<AssessmentOutlinedIcon fontSize="small" />}
      footer={
        <div className="flex justify-end">
          <IconButton size="small" onClick={() => refetch()} disabled={isFetching} aria-label={t("mzr_refresh")}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      }
    >
      <div className="pb-4">{body}</div>
    </Drawer>
  );
}
